<template>
  <sfs-form-modal
    :id="modalId"
    title="Revenue Accrual Report"
    @on-hide="resetForm"
  >
    <b-form
      novalidate
      @submit.stop.prevent="handleSubmit"
    >
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Client"
            label-for="work-order-revenue-accrual-report-client_id"
          >
            <client-select
              id="work-order-revenue-accrual-report-client_id"
              placeholder="All"
              :value="client"
              @input="onClientChange"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.period"
            label-for="work-order-revenue-accrual-report-period"
          >
            <sfs-month-picker
              id="work-order-revenue-accrual-report-period"
              v-model="$v.form.period.$model"
              :with-class="{ 'is-invalid': $v.form.period.$dirty && $v.form.period.$error }"
              :class="{ 'is-invalid': $v.form.period.$dirty && $v.form.period.$error }"
              placeholder="Select Month"
              @on-change="onMonthChange"
            />
            <b-form-invalid-feedback
              v-if="$v.form.period.$dirty"
              id="work-order-revenue-accrual-report-period-feedback"
            >
              <span v-if="!$v.form.period.required">Please select a month.</span>
              <span v-if="!$v.form.period.serverFailed">{{ serverErrors.period_start || serverErrors.period_end }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col
          offset-lg="6"
          lg="3"
          order="2"
          order-lg="1"
        >
          <b-button
            variant="link-dark"
            block
            id="work-order-revenue-accrual-report-close-button"
            :disabled="downloading"
            @click="closeModal"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col
          lg="3"
          order="1"
          order-lg="2"
        >
          <b-button
            id="work-order-revenue-accrual-report-submit-button"
            block
            type="submit"
            variant="primary"
            :disabled="downloading"
          >
            <b-spinner
              v-if="downloading"
              small
              label="Downloading..."
              class="mr-1"
            />
            <i
              v-else
              class="fa fa-download mr-1"
            />
            Download
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </sfs-form-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { DateTime } from 'luxon';
import ClientSelect from '@/components/admin/clients/Select.vue';
import formMixin from '@/mixins/formMixin';
import formModalMixin from '@/mixins/formModalMixin';
import downloadMixin from '@/mixins/downloadMixin';

export default {
  name: 'WorkOrdersRevenueAccrualReportModal',
  components: {
    ClientSelect,
  },
  mixins: [formMixin, formModalMixin, downloadMixin],
  data() {
    return {
      client: null,
      form: {
        client_id: '',
        period: '',
        period_start: '',
        period_end: '',
      },
      fieldLabels: {
        period: 'Accrual Period *',
      },
      modalId: 'work-order-revenue-accrual-report-modal',
    };
  },
  validations: {
    form: {
      client_id: {},
      period: {
        required,
        serverFailed() {
          return !this.hasServerErrors('period_start') && !this.hasServerErrors('period_end');
        },
      },
    },
  },
  methods: {
    handleSubmit() {
      if (this.hasFormErrors()) {
        return;
      }

      this.downloading = true;
      this.$workOrdersAPI
        .getRevenueAccrualSpreadsheet(this.downloadParams())
        .then((response) => {
          this.processDownload(response.data, 'Revenue Accrual Report.csv');
          this.closeModal();
        })
        .catch((error) => {
          this.displayErrorToast(error, {
            fallbackMessage: 'We\'ve experienced an issue while trying to download the file, Please try again.',
          });
          this.downloading = false;
        });
    },
    downloadParams() {
      const params = {
        period_end: this.form.period_end,
      };
      if (this.form.client_id) {
        params.client_id = this.form.client_id;
      }
      return params;
    },
    onClientChange(client) {
      this.form.client_id = client ? client.id.toString() : '';
      this.client = client;
    },
    onMonthChange(date) {
      if (!date) {
        this.form.period = '';
        this.form.period_start = '';
        this.form.period_end = '';
        return;
      }

      const month = DateTime.fromJSDate(date).startOf('month');
      const end = month.endOf('month');
      this.form.period_start = this.$options.filters.dateFormat(month.toISODate());
      this.form.period_end = this.$options.filters.dateFormat(end.toISODate());
    },
    resetForm() {
      this.client = null;
      this.form = {
        client_id: '',
        period: '',
        period_start: '',
        period_end: '',
      };
      this.resetServerErrors();
      this.$v.$reset();
    },
  },
};
</script>
